import React, { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";

// --- Seed Colors ---
const PARTY_COLORS = [
  { label: "Green", color: "#22c55e" },
  { label: "Blue", color: "#3b82f6" },
  { label: "Purple", color: "#a78bfa" },
  { label: "Orange", color: "#fb923c" },
  { label: "Pink", color: "#ec4899" },
];

function randPastel() {
  const h = Math.floor(Math.random() * 360);
  return `hsl(${h},70%,75%)`;
}

// --- Markdown Parser ---
function parseMarkdownFile(content) {
  console.log('=== PARSING MARKDOWN FILE ===');
  console.log('Content length:', content.length);
  
  const lines = content.split('\n');
  console.log('Total lines:', lines.length);
  
  let companyName = '';
  let parties = [];
  let assignments = {};
  let lobApps = [];
  let otherItems = [];
  
  let currentSection = null;
  let inLobSection = false;
  let inOtherSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines but not headers
    if (!line) {
      continue;
    }
    
    // Log all lines for debugging
    console.log(`Line ${i + 1}: "${line}" (currentSection: ${currentSection})`);
    
    // Parse company name
    if (line.startsWith('**Company:**')) {
      companyName = line.replace('**Company:**', '').trim();
      console.log(`Line ${i + 1}: Found company name: "${companyName}"`);
      continue;
    }
    
    // Parse sections BEFORE handling generic headers
    if (line === '## Parties') {
      currentSection = 'parties';
      inLobSection = false;
      inOtherSection = false;
      console.log(`Line ${i + 1}: Entered PARTIES section`);
      continue;
    }
    
    if (line === '## Responsibilities') {
      currentSection = 'responsibilities';
      inLobSection = false;
      inOtherSection = false;
      console.log(`Line ${i + 1}: Entered RESPONSIBILITIES section`);
      continue;
    }
    
    // Handle other headers
    if (line.startsWith('#')) {
      console.log(`Line ${i + 1}: Header found: "${line}"`);
      continue;
    }
    
    // Parse parties
    if (currentSection === 'parties' && line.startsWith('- **')) {
      const match = line.match(/- \*\*(.+?)\*\*(.*)?/);
      if (match) {
        const name = match[1];
        const isCTO = match[2] && match[2].includes('(CTO/CIO/CISO)');
        parties.push({ name, isCTO });
        console.log(`Line ${i + 1}: Found party: "${name}" (CTO: ${isCTO})`);
      }
      continue;
    }
    
    // Parse responsibilities and assignments
    if (currentSection === 'responsibilities' && line.includes('→')) {
      const match = line.match(/^(\s*)- \*\*(.+?)\*\* → (.+?)( \*\(explicit assignment\)\*)?$/);
      if (match) {
        const indent = match[1].length;
        const title = match[2];
        const assignedTo = match[3];
        const isExplicit = !!match[4];
        
        console.log(`Line ${i + 1}: Processing assignment: "${title}" → "${assignedTo}" (indent: ${indent}, explicit: ${isExplicit})`);
        console.log(`  Current state - inLobSection: ${inLobSection}, inOtherSection: ${inOtherSection}`);
        
        // Track when we enter/exit LOB or OTHER sections
        if (title === 'Line of Business Application Administration') {
          inLobSection = true;
          inOtherSection = false;
          console.log(`  >>> Entered LOB section`);
        } else if (title === 'OTHER') {
          inOtherSection = true;
          inLobSection = false;
          console.log(`  >>> Entered OTHER section`);
        } else if (indent === 0 && !inLobSection && !inOtherSection) {
          // Only exit if we're not currently in a special section
          // This allows top-level items within LOB/OTHER sections
        } else if (indent === 0 && (inLobSection || inOtherSection)) {
          // Check if this is a major section change that should exit special sections
          const majorSections = ['IT Leadership', 'Infrastructure Management', 'User Management & Support', 'Gray Area & Specialized Services'];
          if (majorSections.includes(title)) {
            console.log(`  >>> Exited special sections due to major section: ${title}`);
            inLobSection = false;
            inOtherSection = false;
          }
        }
        
        // Extract LOB apps when we're in the LOB section
        if (inLobSection && indent >= 0 && !['Line of Business Application Administration'].includes(title)) {
          // Look for any app-like names in the LOB section (not just "Prod App")
          if (title.match(/^(Prod App|APP|App)/i) || 
              (title.length < 50 && !title.includes('Management') && !title.includes('Control') && !title.includes('Security'))) {
            console.log(`  !!! FOUND LOB APP: "${title}"`);
            if (!lobApps.some(app => app.name === title)) {
              lobApps.push({ name: title });
              console.log(`  !!! ADDED LOB APP to array, count now: ${lobApps.length}`);
            } else {
              console.log(`  !!! LOB APP already exists, skipping`);
            }
          }
        }
        
        // Extract other items when we're in the OTHER section
        if (inOtherSection && indent >= 0 && !['OTHER'].includes(title)) {
          // In OTHER section, capture items that aren't the standard Management/Support/Maintenance
          if (!title.includes('Management') && !title.includes('Support') && !title.includes('Maintenance')) {
            console.log(`  !!! FOUND OTHER ITEM: "${title}"`);
            if (!otherItems.some(item => item.name === title)) {
              otherItems.push({ name: title });
              console.log(`  !!! ADDED OTHER ITEM to array, count now: ${otherItems.length}`);
            } else {
              console.log(`  !!! OTHER ITEM already exists, skipping`);
            }
          }
        }
        
        assignments[title] = { assignedTo, isExplicit };
      } else {
        console.log(`Line ${i + 1}: Line with → didn't match regex: "${line}"`);
      }
    }
  }
  
  console.log('=== PARSING COMPLETE ===');
  console.log('Final results:');
  console.log('Company name:', companyName);
  console.log('Parties found:', parties.length, parties);
  console.log('LOB apps found:', lobApps.length, lobApps);
  console.log('Other items found:', otherItems.length, otherItems);
  console.log('Assignments found:', Object.keys(assignments).length);
  console.log('============================');
  
  return { companyName, parties, assignments, lobApps, otherItems };
}

// --- Full Hierarchy Data (Dynamic) ---
function buildTree(lobApps, otherItems) {
  const lobAppChildren = lobApps.length > 0 
    ? lobApps.map(app => ({ 
        id: `app_${app.id}`, 
        title: app.name || `[App ${app.id}]`,
        children: [
          { id: `app_${app.id}_version`, title: "Version Control" },
          { id: `app_${app.id}_licensing`, title: "Licensing" },
          { id: `app_${app.id}_bdr`, title: "BDR plan / Backup availability" },
          { id: `app_${app.id}_policies`, title: "Global Policies" },
          { id: `app_${app.id}_access`, title: "Access Control" },
          { id: `app_${app.id}_security`, title: "Security Stance report" }
        ]
      }))
    : [{ id: "app_placeholder", title: "No applications defined" }];

  // Also create MDAC entries for LOB apps in Users Management
  const lobAppMDACs = lobApps.length > 0 
    ? lobApps.map(app => ({ 
        id: `mdac_${app.id}`, 
        title: app.name || `[App ${app.id}]`
      }))
    : [{ id: "mdac_placeholder", title: "No applications defined" }];

  // Create Other Items entries for OTHER section
  const otherItemChildren = otherItems.length > 0 
    ? otherItems.map(item => ({ 
        id: `other_${item.id}`, 
        title: item.name || `[Other ${item.id}]`,
        children: [
          { id: `other_${item.id}_management`, title: "Management" },
          { id: `other_${item.id}_support`, title: "Support" },
          { id: `other_${item.id}_maintenance`, title: "Maintenance" }
        ]
      }))
    : [{ id: "other_placeholder", title: "No other items defined" }];

  return {
    id: "root",
    title: "ROOT",
    children: [
      {
        id: "it_leadership",
        title: "IT Leadership",
        children: [
          {
            id: "vision_strategy",
            title: "Vision & Strategy",
            children: [
              {
                id: "direction_planning",
                title: "Direction & Planning",
                children: [
                  { id: "create_it_vision", title: "Create IT vision that supports business goals (IT/Business Alignment)" },
                  { id: "strategy_updates", title: "Regular strategy updates and progress reports (KPI Dashboard)" }
                ]
              },
              {
                id: "goal_setting",
                title: "Goal Setting",
                children: [
                  { id: "annual_quarterly_targets", title: "Set annual and quarterly targets with clear success measures (OKRs - Objectives & Key Results)" },
                  { id: "track_key_metrics", title: "Track key metrics (system uptime, security, costs, user satisfaction) (SLA - Service Level Agreement)" }
                ]
              },
              {
                id: "priority_management",
                title: "Priority Management",
                children: [
                  { id: "annual_roadmap", title: "Create annual roadmap showing what gets done when (IT Roadmap)" },
                  { id: "scope_changes", title: "Manage changes to scope and get leadership approval (Change Control)" }
                ]
              },
              {
                id: "department_scope_definition",
                title: "Department Scope Definition",
                children: [
                  { id: "define_it_scope", title: "Clearly define what IT handles vs what other departments handle (RACI Matrix)" },
                  { id: "set_expectations", title: "Set expectations with business stakeholders (SLA Definition)" },
                  { id: "sourcing_decisions", title: "Decide what to do in-house vs hire outside help (Sourcing Strategy)" }
                ]
              },
              {
                id: "innovation_tech_planning",
                title: "Innovation & Technology Planning",
                children: [
                  { id: "new_tech_contact", title: "Be the main contact for new technology discussions (R&D POC)" },
                  { id: "tech_assessment", title: "Regular assessment: \"Are we keeping up with technology?\" (Technology Maturity Assessment)" },
                  { id: "emerging_tech", title: "Track emerging technologies (AI, automation, digital tools) (Innovation Pipeline)" },
                  {
                    id: "testing_new_tools",
                    title: "Testing new tools and systems safely",
                    children: [
                      { id: "tool_requests", title: "Collect requests for new tools (Intake Process)" },
                      { id: "business_case", title: "Evaluate benefits, risks, costs, and effort required (Business Case Analysis)" },
                      { id: "poc_testing", title: "Test in safe environment first (POC - Proof of Concept)" },
                      { id: "success_criteria", title: "Set clear success/failure criteria (Exit Criteria)" },
                      { id: "go_nogo_decisions", title: "Make go/no-go decisions and plan rollout (Gate Review)" }
                    ]
                  }
                ]
              }
            ]
          },
          {
            id: "governance_standards",
            title: "Governance & Standards",
            children: [
              {
                id: "company_it_policies",
                title: "Company IT Policies",
                children: [
                  {
                    id: "core_policy_framework",
                    title: "Core policy framework",
                    children: [
                      { id: "aup_policy", title: "How employees should use computers and internet (AUP - Acceptable Use Policy)" },
                      { id: "info_security_policy", title: "How to keep company information secure (Information Security Policy)" },
                      { id: "mfa_policy", title: "User login requirements and multi-factor authentication (MFA - Multi-Factor Authentication)" },
                      { id: "password_policy", title: "Password requirements and management (Password Policy)" },
                      { id: "data_classification", title: "How to classify and handle sensitive data (Data Classification Policy)" },
                      { id: "encryption_policy", title: "Data encryption standards (Encryption Policy)" },
                      { id: "byod_policy", title: "Personal device usage at work (BYOD - Bring Your Own Device Policy)" },
                      { id: "remote_work_policy", title: "Working from home IT guidelines (Remote Work Policy)" },
                      { id: "incident_response_policy", title: "What to do when security incidents happen (Incident Response Policy)" },
                      { id: "bcp_drp_policy", title: "Business continuity and disaster recovery (BCP/DRP - Business Continuity/Disaster Recovery Plan)" },
                      { id: "change_mgmt_policy", title: "How to make changes to IT systems safely (Change Management Policy)" },
                      { id: "vendor_risk_mgmt", title: "Working with outside vendors and suppliers (Vendor Risk Management)" },
                      { id: "security_training_policy", title: "IT training requirements for staff (Security Awareness Training)" },
                      { id: "documentation_standards", title: "Documentation and record keeping (Documentation Standards)" }
                    ]
                  },
                  { id: "sop_procedures", title: "Standard operating procedures and documentation (SOPs - Standard Operating Procedures)" },
                  {
                    id: "employee_monitoring",
                    title: "Employee monitoring decisions",
                    children: [
                      { id: "monitoring_scope", title: "What activities will be monitored (Monitoring Scope)" },
                      { id: "legal_basis", title: "Legal requirements and employee rights (Legal Basis)" },
                      { id: "communication_plan", title: "How to communicate monitoring to employees (Communication Plan)" }
                    ]
                  }
                ]
              },
              {
                id: "risk_compliance_mgmt",
                title: "Risk & Compliance Management",
                children: [
                  {
                    id: "risk_management",
                    title: "Risk Management",
                    children: [
                      { id: "risk_register", title: "Track and manage IT risks (who owns what, how we reduce risk) (Risk Register)" },
                      { id: "business_continuity", title: "Business continuity planning (how to keep business running during problems) (BIA - Business Impact Analysis)" },
                      { id: "incident_response", title: "Incident response planning (who does what when problems happen) (IRP - Incident Response Plan)" },
                      {
                        id: "regular_testing",
                        title: "Regular testing schedule",
                        children: [
                          { id: "tabletop_exercises", title: "Practice incident response (twice per year) (Tabletop Exercises)" },
                          { id: "dr_testing", title: "Test disaster recovery (once per year) (DR Testing)" },
                          { id: "restore_testing", title: "Test data backups (every three months) (Restore Testing)" }
                        ]
                      }
                    ]
                  },
                  {
                    id: "legal_regulatory",
                    title: "Legal & Regulatory Compliance",
                    children: [
                      { id: "soc2_iso27001", title: "SOC 2 / ISO 27001 compliance (if required) (Security & Controls Framework)" },
                      { id: "hipaa_compliance", title: "Healthcare data protection - HIPAA (if required) (Health Insurance Portability & Accountability Act)" },
                      { id: "pci_dss", title: "Credit card security - PCI DSS (if required) (Payment Card Industry Data Security Standard)" },
                      { id: "gdpr_ccpa", title: "Privacy regulations - GDPR / CCPA (if required) (General Data Protection Regulation / California Consumer Privacy Act)" },
                      { id: "vendor_dpa", title: "Vendor data protection agreements and assessments (DPA - Data Processing Agreements)" }
                    ]
                  },
                  {
                    id: "security_management",
                    title: "Security Management",
                    children: [
                      { id: "security_assessment", title: "Regular security assessment: \"How secure are we right now?\" (Security Posture Assessment)" },
                      { id: "security_roadmap", title: "Security improvement roadmap (multi-factor auth, threat detection, etc.) (Security Program Roadmap)" },
                      { id: "zero_trust", title: "Zero-Trust security approach decision and implementation scope (Zero Trust Architecture)" }
                    ]
                  }
                ]
              },
              {
                id: "financial_management",
                title: "Financial Management",
                children: [
                  {
                    id: "it_budget_planning",
                    title: "IT Budget Planning",
                    children: [
                      {
                        id: "budget_categories",
                        title: "Budget categories to plan for",
                        children: [
                          { id: "staff_contractors", title: "Staff salaries and contractors (FTE - Full Time Equivalent)" },
                          { id: "training_certs", title: "Employee training and certifications (CPE - Continuing Professional Education)" },
                          { id: "software_licenses", title: "Software licenses and subscriptions (SaaS - Software as a Service)" },
                          { id: "cloud_costs", title: "Cloud services usage costs (IaaS/PaaS - Infrastructure/Platform as a Service)" },
                          { id: "hardware_equipment", title: "New hardware and equipment (CAPEX - Capital Expenditures)" },
                          { id: "maintenance_support", title: "Equipment maintenance and support contracts (OPEX - Operating Expenditures)" },
                          { id: "telecom_services", title: "Phone and internet services (Telecom)" },
                          { id: "security_tools", title: "Security tools and monitoring services (MDR/SIEM - Managed Detection Response/Security Information Event Management)" },
                          { id: "backup_costs", title: "Backup and disaster recovery costs (BaaS - Backup as a Service)" },
                          { id: "emergency_fund", title: "Emergency fund for unexpected costs (Contingency Budget)" }
                        ]
                      },
                      { id: "budget_tracking", title: "Monthly budget tracking and variance reporting (Budget Management)" }
                    ]
                  },
                  {
                    id: "vendor_purchasing",
                    title: "Vendor & Purchasing Management",
                    children: [
                      { id: "vendor_selection", title: "Vendor selection and approval process (Vendor Management)" },
                      { id: "vendor_relationship", title: "Main point of contact for IT vendor relationships (Vendor Relationship Management)" },
                      { id: "contract_management", title: "Vendor billing and contract management (Contract Management)" },
                      { id: "vendor_risk", title: "Vendor risk assessment and exit strategies (Vendor Risk Management)" },
                      { id: "p2p_workflow", title: "Purchase order through invoice payment workflow (P2P - Procure to Pay)" }
                    ]
                  }
                ]
              }
            ]
          },
          {
            id: "team_culture",
            title: "Team & Culture Management",
            children: [
              {
                id: "team_development",
                title: "Team Development",
                children: [
                  { id: "skills_gaps", title: "Identify skills gaps and create development plans for staff (IDP - Individual Development Plan)" },
                  { id: "training_budget", title: "Plan and budget for employee training and certifications (CPE - Continuing Professional Education)" },
                  { id: "staffing_needs", title: "IT staffing needs determination and hiring plans (Resource Planning)" }
                ]
              },
              {
                id: "succession_planning",
                title: "Succession Planning",
                children: [
                  { id: "key_roles_coverage", title: "Identify who can cover key roles when people are unavailable (Succession Planning)" },
                  { id: "documentation_handover", title: "Keep up-to-date documentation and handover procedures (Knowledge Transfer)" }
                ]
              },
              {
                id: "department_representation",
                title: "Department Representation",
                children: [
                  { id: "executive_updates", title: "Provide regular updates to executives (Executive Reporting)" },
                  { id: "budget_defense", title: "Justify IT budget and spending (Budget Defense)" },
                  { id: "stakeholder_liaison", title: "Act as IT advocate and liaison with other departments (Stakeholder Management)" }
                ]
              },
              {
                id: "cross_department",
                title: "Cross-Department Coordination",
                children: [
                  { id: "hr_coordination", title: "HR Department coordination (onboarding, role changes, approvals) (Identity Lifecycle Management)" },
                  { id: "finance_coordination", title: "Finance Department coordination (purchasing, licensing, cost allocation) (Financial Coordination)" },
                  { id: "legal_coordination", title: "Legal/Compliance coordination (policies, assessments, holds) (Compliance Coordination)" },
                  { id: "operations_coordination", title: "Operations coordination (maintenance windows, change notifications) (Operational Coordination)" }
                ]
              }
            ]
          }
        ]
      },
      {
        id: "it_dept_mgmt",
        title: "IT Department Management",
        children: [
          {
            id: "process_management",
            title: "Process Management",
            children: [
              {
                id: "core_it_services",
                title: "Core IT Service Processes",
                children: [
                  { id: "incident_mgmt", title: "Incident management (fixing problems quickly) (ITIL Incident Management)" },
                  { id: "service_requests", title: "Service requests (fulfilling user requests) (ITIL Request Fulfillment)" },
                  { id: "problem_mgmt", title: "Problem management (preventing recurring issues) (ITIL Problem Management)" },
                  { id: "change_mgmt", title: "Change management (safely implementing changes) (ITIL Change Management)" },
                  { id: "release_mgmt", title: "Release management (deploying new software/systems) (ITIL Release Management)" }
                ]
              },
              {
                id: "user_lifecycle",
                title: "User Lifecycle Processes",
                children: [
                  { id: "onboarding_tasks", title: "Employee onboarding IT tasks (Account Provisioning)" },
                  { id: "offboarding_tasks", title: "Employee offboarding IT tasks (Account Deprovisioning)" },
                  { id: "access_updates", title: "Role change and access update processes (Access Management)" }
                ]
              },
              {
                id: "quality_mgmt",
                title: "Quality Management",
                children: [
                  { id: "process_assessment", title: "Regular process assessment: \"Are our procedures working well?\" (Process Maturity Assessment)" },
                  { id: "service_quality", title: "Service quality tracking and improvement (Service Quality Management)" },
                  { id: "continuous_improvement", title: "Continuous improvement tracking and procedure reviews (CI - Continuous Improvement)" }
                ]
              }
            ]
          },
          {
            id: "asset_inventory",
            title: "Asset & Inventory Management",
            children: [
              {
                id: "equipment_procurement",
                title: "Equipment Procurement",
                children: [
                  { id: "equipment_sourcing", title: "Equipment sourcing and ordering (Procurement)" },
                  { id: "order_tracking", title: "Order tracking and delivery notifications (Order Management)" },
                  { id: "asset_tagging", title: "Equipment receiving and asset tagging (Asset Registration)" },
                  { id: "warranty_contracts", title: "Warranty and support contract management (Contract Management)" }
                ]
              },
              {
                id: "inventory_mgmt",
                title: "Inventory Management",
                children: [
                  { id: "hardware_inventory", title: "Hardware inventory tracking (computers, phones, tablets) (Asset Inventory)" },
                  { id: "license_inventory", title: "Software license inventory and compliance (License Management)" },
                  { id: "network_inventory", title: "Network equipment inventory (firewalls, switches, access points) (Network Inventory)" }
                ]
              },
              {
                id: "lifecycle_mgmt",
                title: "Lifecycle Management",
                children: [
                  { id: "refresh_planning", title: "Equipment refresh planning and scheduling (Refresh Planning)" },
                  { id: "eol_planning", title: "End-of-life equipment identification (EOL Planning)" },
                  { id: "asset_disposal", title: "Asset disposal and decommissioning (Asset Disposal)" }
                ]
              },
              {
                id: "approved_standards",
                title: "Approved Standards Management",
                children: [
                  { id: "software_standards", title: "Maintain approved software list (Software Standards)" },
                  { id: "vendor_standards", title: "Maintain approved vendor list (Vendor Standards)" },
                  { id: "hardware_standards", title: "Maintain approved equipment list (Hardware Standards)" }
                ]
              }
            ]
          },
          {
            id: "core_services",
            title: "Core Services Management",
            children: [
              {
                id: "public_services",
                title: "Public-facing Services",
                children: [
                  { id: "dns_mgmt", title: "Public DNS management (DNS Management)" },
                  { id: "web_hosting", title: "Website hosting management (Web Hosting)" },
                  { id: "web_performance", title: "Website availability and performance planning (Web Performance)" }
                ]
              },
              {
                id: "essential_infrastructure",
                title: "Essential Infrastructure Services",
                children: [
                  { id: "email_services", title: "Email and collaboration platform management (Email Services)" },
                  { id: "document_mgmt", title: "File sharing and document management (Document Management)" },
                  { id: "app_services", title: "Core business application availability (Application Services)" }
                ]
              }
            ]
          }
        ]
      },
      {
        id: "it_project_mgmt",
        title: "IT Project Management",
        children: [
          {
            id: "project_portfolio",
            title: "Project Portfolio Management",
            children: [
              { id: "project_pipeline", title: "Project intake and pipeline management (Project Pipeline)" },
              { id: "resource_allocation", title: "Project prioritization and resource allocation (Resource Management)" },
              { id: "portfolio_planning", title: "Project portfolio planning and governance (Portfolio Planning)" },
              { id: "benefits_mgmt", title: "Project ROI tracking and benefits realization (Benefits Management)" }
            ]
          },
          {
            id: "project_execution",
            title: "Project Execution",
            children: [
              { id: "project_planning", title: "Project planning and scope definition (Project Planning)" },
              { id: "project_communication", title: "Project coordination and communication (Project Communication)" },
              { id: "project_qa", title: "Project monitoring and quality assurance (Project QA)" },
              { id: "project_delivery", title: "Project delivery and closure (Project Delivery)" },
              { id: "project_review", title: "Post-project review and continuous improvement (Project Review)" }
            ]
          },
          {
            id: "tech_implementation",
            title: "Technology Implementation Projects",
            children: [
              { id: "infrastructure_projects", title: "Infrastructure upgrade projects (Infrastructure Projects)" },
              { id: "software_projects", title: "Software implementation and integration projects (Software Projects)" },
              { id: "security_projects", title: "Security enhancement projects (Security Projects)" },
              { id: "automation_projects", title: "Business process automation projects (Automation Projects)" }
            ]
          }
        ]
      },
      {
        id: "infrastructure_mgmt",
        title: "Infrastructure Management",
        children: [
          {
            id: "core_infrastructure",
            title: "Core Infrastructure",
            children: [
              {
                id: "it_mgmt_tools",
                title: "IT Management Tools",
                children: [
                  { id: "rmm_platform", title: "Remote monitoring and management platform (RMM - Remote Monitoring & Management)" },
                  { id: "backup_systems", title: "Backup and recovery systems (Backup Solutions)" },
                  { id: "edr_protection", title: "Antivirus and endpoint protection (EDR - Endpoint Detection & Response)" },
                  { id: "mdm_platform", title: "Mobile device management platform (MDM - Mobile Device Management)" }
                ]
              },
              {
                id: "endpoint_mgmt",
                title: "Endpoint Management",
                children: [
                  {
                    id: "computer_mgmt",
                    title: "Computer Management",
                    children: [
                      { id: "endpoint_backup", title: "Backup and disaster recovery management (Endpoint Backup)" },
                      { id: "patch_mgmt", title: "Software updates and patch management (Patch Management)" },
                      { id: "av_mgmt", title: "Antivirus monitoring and alert response (AV Management)" },
                      { id: "local_accounts", title: "User account management on devices (Local Account Management)" },
                      { id: "device_lifecycle", title: "Equipment inventory and lifecycle tracking (Device Lifecycle)" }
                    ]
                  },
                  {
                    id: "mobile_device_mgmt",
                    title: "Mobile Device Management",
                    children: [
                      { id: "mobile_mgmt", title: "Phone and tablet management through MDM (Mobile Management)" },
                      { id: "mobile_inventory", title: "Mobile device inventory tracking (Mobile Inventory)" },
                      { id: "mobile_lifecycle", title: "Mobile device lifecycle and replacement planning (Mobile Lifecycle)" }
                    ]
                  }
                ]
              },
              {
                id: "network_infrastructure",
                title: "Network Infrastructure",
                children: [
                  {
                    id: "firewall_mgmt",
                    title: "Firewall Management",
                    children: [
                      { id: "firewall_monitoring", title: "Firewall uptime planning and monitoring (Firewall Monitoring)" },
                      { id: "firewall_maintenance", title: "Firewall patching and maintenance windows (Firewall Maintenance)" },
                      { id: "acl_mgmt", title: "Access control list management (ACL Management)" },
                      { id: "firewall_lifecycle", title: "Firewall inventory and lifecycle planning (Firewall Lifecycle)" }
                    ]
                  },
                  {
                    id: "isp_mgmt",
                    title: "Internet Service Provider Management",
                    children: [
                      { id: "isp_monitoring", title: "ISP uptime monitoring and reporting (ISP Monitoring)" },
                      { id: "isp_vendor_mgmt", title: "ISP vendor communication and issue escalation (ISP Management)" },
                      { id: "bandwidth_mgmt", title: "Internet throughput testing and optimization (Bandwidth Management)" }
                    ]
                  },
                  {
                    id: "network_equipment",
                    title: "Network Equipment Management",
                    children: [
                      { id: "network_uptime", title: "Router and switch uptime planning (Network Uptime)" },
                      { id: "network_maintenance", title: "Network equipment patching and maintenance (Network Maintenance)" },
                      { id: "network_lifecycle", title: "Network equipment inventory and lifecycle (Network Lifecycle)" },
                      { id: "network_performance", title: "Network capacity and performance monitoring (Network Performance)" }
                    ]
                  },
                  {
                    id: "wireless_infrastructure",
                    title: "Wireless Infrastructure",
                    children: [
                      { id: "wireless_planning", title: "Wi-Fi network planning and optimization (Wireless Planning)" },
                      { id: "ssid_mgmt", title: "Wireless network management and security (SSID Management)" },
                      { id: "wireless_monitoring", title: "Wi-Fi availability and load monitoring (Wireless Monitoring)" },
                      { id: "ap_lifecycle", title: "Access point inventory and lifecycle management (AP Lifecycle)" }
                    ]
                  },
                  {
                    id: "cabling_physical",
                    title: "Network Cabling & Physical Infrastructure",
                    children: [
                      { id: "cabling_infrastructure", title: "Network drop planning and deployment (Cabling Infrastructure)" },
                      { id: "physical_infrastructure", title: "Physical network infrastructure maintenance (Physical Infrastructure)" }
                    ]
                  },
                  {
                    id: "remote_access",
                    title: "Remote Access Infrastructure",
                    children: [
                      { id: "vpn_mgmt", title: "VPN endpoint management and access control (VPN Management)" },
                      { id: "vpn_maintenance", title: "VPN patching and security updates (VPN Maintenance)" },
                      { id: "vpn_monitoring", title: "VPN availability monitoring and troubleshooting (VPN Monitoring)" }
                    ]
                  }
                ]
              }
            ]
          },
          {
            id: "cloud_infrastructure",
            title: "Cloud Infrastructure",
            children: [
              {
                id: "microsoft_cloud",
                title: "Microsoft Cloud Services (Azure/Office 365)",
                children: [
                  { id: "cloud_governance", title: "Global cloud policies and governance (Cloud Governance)" },
                  { id: "license_optimization", title: "License management and optimization (License Optimization)" },
                  { id: "exchange_mgmt", title: "Email and Exchange management (Exchange Management)" },
                  { id: "email_security", title: "Spam filtering and email security (Email Security)" },
                  { id: "cloud_backup", title: "Cloud data backups and recovery (Cloud Backup)" },
                  { id: "collaboration_platform", title: "SharePoint and OneDrive management (Collaboration Platform)" }
                ]
              },
              {
                id: "other_cloud",
                title: "Other Cloud Platforms",
                children: [
                  { id: "aws_mgmt", title: "AWS infrastructure management (AWS Management)" },
                  { id: "saas_mgmt", title: "Third-party SaaS application management (SaaS Management)" },
                  { id: "multicloud_strategy", title: "Multi-cloud strategy and integration (Multi-cloud Strategy)" }
                ]
              }
            ]
          },
          {
            id: "communication_infrastructure",
            title: "Communication Infrastructure",
            children: [
              {
                id: "voice_communication",
                title: "Voice Communication Systems",
                children: [
                  { id: "voip_mgmt", title: "Phone system and VOIP management (VOIP Management)" },
                  { id: "call_flow_mgmt", title: "Call flow configuration and optimization (Call Flow Management)" },
                  { id: "ivr_mgmt", title: "Interactive voice response system management (IVR Management)" },
                  { id: "number_mgmt", title: "Phone number porting and provisioning (Number Management)" }
                ]
              },
              {
                id: "mobile_communication",
                title: "Mobile Communication Management",
                children: [
                  { id: "carrier_mgmt", title: "Cellular provider relationship management (Carrier Management)" },
                  { id: "mobile_provisioning", title: "Mobile equipment requests and provisioning (Mobile Provisioning)" },
                  { id: "mobile_billing", title: "Mobile line management and billing (Mobile Billing)" },
                  { id: "byod_mgmt", title: "Non-MDM device management (BYOD Management)" }
                ]
              }
            ]
          },
          {
            id: "facilities_it",
            title: "Facilities IT Infrastructure",
            children: [
              {
                id: "office_technology",
                title: "Office Technology",
                children: [
                  { id: "print_mgmt", title: "Printer management and maintenance (Print Management)" },
                  { id: "phone_mgmt", title: "Desk phone management (Phone Management)" },
                  { id: "camera_mgmt", title: "Security camera systems and lifecycle (Camera Management)" },
                  { id: "access_control", title: "Physical access control systems (Access Control Systems)" },
                  { id: "av_mgmt", title: "Conference room AV and display systems (AV Management)" }
                ]
              },
              {
                id: "specialized_equipment",
                title: "Specialized Equipment",
                children: [
                  { id: "iot_device_mgmt", title: "IP-enabled machinery and specialized equipment management (IoT Device Management)" }
                ]
              }
            ]
          }
        ]
      },
      {
        id: "lob_administration",
        title: "Line of Business Application Administration",
        children: lobAppChildren
      },
      {
        id: "user_mgmt_support",
        title: "User Management & Support",
        children: [
          {
            id: "account_access",
            title: "Account & Access Management",
            children: [
              {
                id: "user_account_admin",
                title: "User Account Administration",
                children: [
                  {
                    id: "o365_admin",
                    title: "Office 365 user account management (O365 Administration)",
                    children: [
                      { id: "email_signature_mgmt", title: "Email signature management and templates (Email Signature Management)" },
                      { id: "shared_mailbox_mgmt", title: "Shared mailbox creation and management (Shared Mailbox Management)" },
                      { id: "group_mgmt", title: "Distribution and security group management (Group Management)" }
                    ]
                  },
                  { id: "identity_mgmt", title: "Access control across all systems (Identity Management)" },
                  { id: "telecom_access", title: "Telecommunications access management (Telecom Access)" },
                  {
                    id: "app_access",
                    title: "Business application access management (Application Access)",
                    children: lobAppMDACs
                  }
                ]
              },
              {
                id: "password_security",
                title: "Password & Security Management",
                children: [
                  { id: "password_vault", title: "Password management system administration (Password Vault)" },
                  { id: "mfa_admin", title: "Multi-factor authentication management (MFA Administration)" },
                  { id: "certificate_mgmt", title: "Security token and certificate management (Certificate Management)" }
                ]
              },
              {
                id: "user_onboarding_training",
                title: "User Onboarding & Training",
                children: [
                  { id: "it_onboarding", title: "New employee IT orientation sessions (IT Onboarding)" },
                  { id: "security_training", title: "IT security training and awareness programs (Security Training)" },
                  { id: "app_training", title: "Procedural training for business applications (Application Training)" }
                ]
              }
            ]
          },
          {
            id: "helpdesk_support",
            title: "Help Desk & Support Services",
            children: [
              {
                id: "helpdesk_operations",
                title: "Help Desk Operations",
                children: [
                  { id: "ticket_triage", title: "Incoming request triage and prioritization (Ticket Triage)" },
                  { id: "business_hours_support", title: "Phone and remote support during business hours (Business Hours Support)" },
                  { id: "after_hours_support", title: "After-hours and emergency support (After-hours Support)" },
                  { id: "helpdesk_slas", title: "Help desk service level agreements and metrics (Help Desk SLAs)" }
                ]
              },
              {
                id: "desktop_onsite",
                title: "Desktop & On-site Support",
                children: [
                  { id: "onsite_support", title: "On-site support during business hours (On-site Support)" },
                  { id: "emergency_onsite", title: "After-hours on-site emergency support (Emergency On-site)" },
                  { id: "desktop_slas", title: "Desktop support service level agreements (Desktop SLAs)" }
                ]
              },
              {
                id: "proactive_support",
                title: "Proactive Support Services",
                children: [
                  { id: "spam_mgmt", title: "Email spam filter review and management (Spam Management)" },
                  { id: "proactive_monitoring", title: "System health monitoring and proactive alerts (Proactive Monitoring)" },
                  { id: "user_experience", title: "User satisfaction surveys and feedback collection (User Experience)" }
                ]
              }
            ]
          },
          {
            id: "communication_training",
            title: "Communication & Training",
            children: [
              {
                id: "user_communication",
                title: "User Communication",
                children: [
                  { id: "incident_communications", title: "IT issue alerts and status notifications (Incident Communications)" },
                  { id: "it_communications", title: "Regular IT newsletter and updates (IT Communications)" },
                  { id: "change_communications", title: "Change management notifications and training (Change Communications)" }
                ]
              },
              {
                id: "training_program",
                title: "Training Program Management",
                children: [
                  { id: "security_training_program", title: "Security awareness training programs (Security Training Program)" },
                  { id: "system_training", title: "Software and system training coordination (System Training)" },
                  { id: "training_assessment", title: "Training effectiveness measurement and improvement (Training Assessment)" }
                ]
              }
            ]
          }
        ]
      },
      {
        id: "gray_specialized",
        title: "Gray Area & Specialized Services",
        children: [
          {
            id: "app_dev_services",
            title: "Application Development Services",
            children: [
              {
                id: "custom_app_dev",
                title: "Custom Application Development",
                children: [
                  { id: "requirements_analysis", title: "Business requirements gathering and analysis (Requirements Analysis)" },
                  { id: "solution_architecture", title: "Application design and architecture planning (Solution Architecture)" },
                  { id: "development_services", title: "Custom application development and integration (Development Services)" },
                  { id: "qa_testing", title: "Application testing and quality assurance (QA Testing)" },
                  { id: "app_support", title: "Application maintenance and ongoing support (Application Support)" }
                ]
              },
              {
                id: "app_integration",
                title: "Application Integration Services",
                children: [
                  { id: "integration_services", title: "System integration planning and implementation (Integration Services)" },
                  { id: "data_migration", title: "Data migration and synchronization (Data Migration)" },
                  { id: "api_mgmt", title: "API management and development (API Management)" }
                ]
              }
            ]
          },
          {
            id: "data_mgmt_services",
            title: "Data Management Services",
            children: [
              {
                id: "database_admin",
                title: "Database Administration",
                children: [
                  { id: "database_architecture", title: "Database design and architecture (Database Architecture)" },
                  { id: "database_performance", title: "Database performance monitoring and optimization (Database Performance)" },
                  { id: "database_security", title: "Database security and access control (Database Security)" },
                  { id: "database_bdr", title: "Database backup and recovery management (Database BDR)" },
                  { id: "database_maintenance", title: "Database maintenance and support (Database Maintenance)" }
                ]
              },
              {
                id: "business_intelligence",
                title: "Business Intelligence & Analytics",
                children: [
                  { id: "data_integration", title: "Data collection and integration (Data Integration)" },
                  { id: "data_modeling", title: "Data modeling and warehouse design (Data Modeling)" },
                  { id: "bi_reporting", title: "Analytics and reporting solutions (BI Reporting)" },
                  { id: "data_analytics", title: "Strategic insights and decision support (Data Analytics)" }
                ]
              }
            ]
          },
          {
            id: "digital_services",
            title: "Digital Services",
            children: [
              {
                id: "website_mgmt",
                title: "Website Management",
                children: [
                  { id: "web_design", title: "Website design and user experience (Web Design)" },
                  { id: "content_mgmt", title: "Website content management (Content Management)" },
                  { id: "web_infrastructure", title: "Website hosting and infrastructure (Web Infrastructure)" },
                  { id: "web_performance_monitoring", title: "Website availability and performance monitoring (Web Performance)" }
                ]
              },
              {
                id: "digital_marketing",
                title: "Digital Marketing Technology",
                children: [
                  { id: "marketing_technology", title: "Marketing automation platform management (Marketing Technology)" },
                  { id: "crm_mgmt", title: "Customer relationship management systems (CRM Management)" },
                  { id: "ecommerce_mgmt", title: "E-commerce platform management (E-commerce Management)" }
                ]
              }
            ]
          },
          {
            id: "operational_support",
            title: "Operational Support Services",
            children: [
              {
                id: "physical_operations",
                title: "Physical Operations",
                children: [
                  { id: "storage_mgmt", title: "Equipment storage and warehouse management (Storage Management)" },
                  { id: "logistics_coordination", title: "Shipping and receiving coordination (Logistics Coordination)" },
                  { id: "facilities_support", title: "Facilities non-IT infrastructure support (Facilities Support)" }
                ]
              },
              {
                id: "specialized_services",
                title: "Specialized Services",
                children: [
                  { id: "process_automation", title: "Business process automation (Process Automation)" },
                  { id: "document_services", title: "Document management and digitization (Document Services)" },
                  { id: "audit_support", title: "Compliance and audit support services (Audit Support)" }
                ]
              }
            ]
          }
        ]
      },
      {
        id: "other_section",
        title: "OTHER",
        children: otherItemChildren
      }
    ]
  };
}

// --- Helpers ---
function walk(node, fn, path = []) {
  fn(node, path);
  node.children?.forEach((c) => walk(c, fn, [...path, node]));
}

function findNodeById(node, id) {
  if (node.id === id) return node;
  for (const c of node.children || []) {
    const f = findNodeById(c, id);
    if (f) return f;
  }
  return null;
}

// --- UI ---
function App() {
  console.log('=== APP COMPONENT INITIALIZING ===');
  
  // Global error handler
  window.addEventListener('error', (e) => {
    console.error('=== GLOBAL JAVASCRIPT ERROR ===', e.error, e.message, e.filename, e.lineno);
  });
  
  window.addEventListener('unhandledrejection', (e) => {
    console.error('=== UNHANDLED PROMISE REJECTION ===', e.reason);
  });
  
  const [companyName, setCompanyName] = useState("");
  const [parties, setParties] = useState([
    { id: "cto", name: "", color: PARTY_COLORS[0].color, confirmed: false },
    { id: "p1", name: "", color: PARTY_COLORS[1].color, confirmed: false },
    { id: "p2", name: "", color: PARTY_COLORS[2].color, confirmed: false }
  ]);
  
  const [lobApps, setLobApps] = useState([
    { id: "app1", name: "", confirmed: false },
    { id: "app2", name: "", confirmed: false }
  ]);

  const [otherItems, setOtherItems] = useState([
    { id: "other1", name: "", confirmed: false },
    { id: "other2", name: "", confirmed: false }
  ]);

  const [open, setOpen] = useState({});
  const [assign, setAssign] = useState({});
  
  // Scroll position preservation
  const scrollPositionRef = useRef(0);
  const [preserveScroll, setPreserveScroll] = useState(false);
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = useRef(null);

  // Generate dynamic tree based on LOB apps and other items
  const TREE = useMemo(() => buildTree(lobApps, otherItems), [lobApps, otherItems]);
  
  // Restore scroll position after assignment updates
  useLayoutEffect(() => {
    if (preserveScroll && scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current);
      setPreserveScroll(false);
    }
  }, [assign, preserveScroll]);

  const selectableParties = parties;
  const idToParty = useMemo(() => Object.fromEntries(parties.map((p) => [p.id, p])), [parties]);

  // initialize assignment map - now depends on TREE
  useEffect(() => {
    const newAssign = {};
    walk(TREE, (n) => {
      // Preserve existing assignments, only add new ones
      newAssign[n.id] = assign[n.id] || { partyId: null, explicit: false };
    });
    
    // Only update if there are actually new nodes
    const hasNewNodes = Object.keys(newAssign).some(key => !assign[key]);
    if (hasNewNodes) {
      setAssign(newAssign);
    }
  }, [TREE, assign]);

  function propagate(nodeId, partyId, explicitAtNode = true) {
    const target = findNodeById(TREE, nodeId);
    if (!target) return;
    const updated = { ...assign };
    function setSubtree(n, isRoot) {
      updated[n.id] = { partyId, explicit: isRoot ? explicitAtNode : false };
      n.children?.forEach((c) => setSubtree(c, false));
    }
    setSubtree(target, true);
    setAssign(updated);
  }

  function confirmCTO() {
    // Capture current scroll position
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    setPreserveScroll(true);
    
    setParties((prev) => prev.map((p) => (p.id === "cto" ? { ...p, confirmed: true } : p)));
    propagate("root", "cto", true);
  }

  function confirmParty(id) {
    setParties((prev) => prev.map((p) => (p.id === id ? { ...p, confirmed: true } : p)));
  }

  function deleteParty(id) {
    if (id === "cto") return;
    const updated = { ...assign };
    Object.keys(updated).forEach((k) => {
      if (updated[k].partyId === id) updated[k] = { partyId: null, explicit: updated[k].explicit };
    });
    setAssign(updated);
    setParties((prev) => prev.filter((p) => p.id !== id));
  }

  function addParty() {
    const newId = `party_${Math.random().toString(36).slice(2, 8)}`;
    setParties((prev) => [...prev, { id: newId, name: "", color: randPastel(), confirmed: false }]);
  }

  // LOB Apps management functions
  function confirmLobApp(id) {
    setLobApps((prev) => prev.map((app) => (app.id === id ? { ...app, confirmed: true } : app)));
  }

  function deleteLobApp(id) {
    // Remove any assignments to app nodes that will be deleted
    const updated = { ...assign };
    Object.keys(updated).forEach((k) => {
      if (k.startsWith(`app_${id}`)) {
        delete updated[k];
      }
    });
    setAssign(updated);
    setLobApps((prev) => prev.filter((app) => app.id !== id));
  }

  function addLobApp() {
    const newId = `app${Date.now()}`;
    setLobApps((prev) => [...prev, { id: newId, name: "", confirmed: false }]);
  }

  // Other Items management functions
  function confirmOtherItem(id) {
    setOtherItems((prev) => prev.map((item) => (item.id === id ? { ...item, confirmed: true } : item)));
  }

  function deleteOtherItem(id) {
    // Remove any assignments to other item nodes that will be deleted
    const updated = { ...assign };
    Object.keys(updated).forEach((k) => {
      if (k.startsWith(`other_${id}`)) {
        delete updated[k];
      }
    });
    setAssign(updated);
    setOtherItems((prev) => prev.filter((item) => item.id !== id));
  }

  function addOtherItem() {
    const newId = `other${Date.now()}`;
    setOtherItems((prev) => [...prev, { id: newId, name: "", confirmed: false }]);
  }
  
  // Import functionality
  function handleFileSelect(event) {
    console.log('=== FILE SELECT EVENT ===');
    console.log('Event:', event);
    console.log('Files:', event.target.files);
    const file = event.target.files[0];
    console.log('Selected file:', file);
    
    if (file) {
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
    }
    
    if (file && (file.type === 'text/markdown' || file.name.endsWith('.md'))) {
      console.log('File accepted as markdown');
      setSelectedFile(file);
      setImportStatus('');
    } else {
      console.log('File rejected - not markdown');
      setImportStatus('Please select a valid .md file');
      setSelectedFile(null);
    }
  }
  
  function processImportFile() {
    console.log('=== PROCESS IMPORT FILE ===');
    console.log('Selected file:', selectedFile);
    
    if (!selectedFile) {
      console.log('No file selected, aborting');
      setImportStatus('No file selected');
      return;
    }
    
    console.log('Creating FileReader...');
    const reader = new FileReader();
    
    reader.onerror = (e) => {
      console.error('FileReader error:', e);
      setImportStatus('Error reading file');
    };
    
    reader.onload = (e) => {
      console.log('=== FILE READER ONLOAD ===');
      console.log('FileReader result length:', e.target.result?.length);
      
      try {
        const content = e.target.result;
        console.log('About to call parseMarkdownFile with content length:', content.length);
        const parsed = parseMarkdownFile(content);
        
        // Debug: Log what we parsed
        console.log('Parsed data:', {
          companyName: parsed.companyName,
          parties: parsed.parties,
          lobApps: parsed.lobApps,
          otherItems: parsed.otherItems,
          assignmentCount: Object.keys(parsed.assignments).length
        });
        
        // Set company name first
        if (parsed.companyName) {
          setCompanyName(parsed.companyName);
        }
        
        // Process imports sequentially with proper delays
        const processImports = async () => {
          let newParties = [];
          let newLobApps = [];
          let newOtherItems = [];
          
          // Step 1: Set parties and wait
          if (parsed.parties.length > 0) {
            newParties = parsed.parties.map((party, index) => {
              if (party.isCTO) {
                return {
                  id: 'cto',
                  name: party.name,
                  color: PARTY_COLORS[0].color,
                  confirmed: true
                };
              } else {
                return {
                  id: `party_${Date.now()}_${index}`,
                  name: party.name,
                  color: index < PARTY_COLORS.length ? PARTY_COLORS[index].color : randPastel(),
                  confirmed: true
                };
              }
            });
            setParties(newParties);
            setImportStatus(`Step 1/4: Imported ${parsed.parties.length} parties...`);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          // Step 2: Set LOB apps and wait
          if (parsed.lobApps.length > 0) {
            newLobApps = parsed.lobApps.map((app, index) => ({
              id: `app_import_${Date.now()}_${index}`,
              name: app.name,
              confirmed: true
            }));
            setLobApps(newLobApps);
            setImportStatus(`Step 2/4: Imported ${parsed.lobApps.length} LOB apps...`);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          // Step 3: Set other items and wait
          if (parsed.otherItems.length > 0) {
            newOtherItems = parsed.otherItems.map((item, index) => ({
              id: `other_import_${Date.now()}_${index}`,
              name: item.name,
              confirmed: true
            }));
            setOtherItems(newOtherItems);
            setImportStatus(`Step 3/4: Imported ${parsed.otherItems.length} other items...`);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          // Step 4: Apply assignments after all state updates
          setImportStatus(`Step 4/4: Applying ${Object.keys(parsed.assignments).length} assignments...`);
          await new Promise(resolve => setTimeout(resolve, 500)); // Extra wait for tree rebuild
          
          // Build party mapping
          const partyMap = {};
          newParties.forEach(party => {
            partyMap[party.name] = party.id;
          });
          
          // Function to find nodes by title in the tree
          function findNodesByTitle(node, targetTitle) {
            const matches = [];
            if (node.title === targetTitle) {
              matches.push(node);
            }
            if (node.children) {
              node.children.forEach(child => {
                matches.push(...findNodesByTitle(child, targetTitle));
              });
            }
            return matches;
          }
          
          // Build current tree with updated items
          const currentTree = buildTree(
            newLobApps.length > 0 ? newLobApps : lobApps,
            newOtherItems.length > 0 ? newOtherItems : otherItems
          );
          
          // Apply assignments from parsed data
          const newAssign = { ...assign };
          const processedTitles = new Set();
          let assignmentsApplied = 0;
          
          Object.entries(parsed.assignments).forEach(([title, assignment]) => {
            if (processedTitles.has(title)) return;
            processedTitles.add(title);
            
            const partyId = partyMap[assignment.assignedTo];
            if (partyId) {
              const matchingNodes = findNodesByTitle(currentTree, title);
              matchingNodes.forEach(node => {
                newAssign[node.id] = { 
                  partyId: partyId, 
                  explicit: assignment.isExplicit || false 
                };
                assignmentsApplied++;
              });
            }
          });
          
          setAssign(newAssign);
          
          // Final success message
          setImportStatus(`✓ Successfully imported: ${parsed.parties.length} parties, ${parsed.lobApps.length} apps, ${parsed.otherItems.length} other items, ${assignmentsApplied} assignments applied`);
        };
        
        // Start the sequential import process
        processImports().catch(error => {
          console.error('Import process failed:', error);
          setImportStatus(`Error during import: ${error.message}`);
        });
        
        // Close modal after a short delay
        setTimeout(() => {
          setShowImportModal(false);
          setSelectedFile(null);
          setImportStatus('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 2000);
        
      } catch (error) {
        console.error('Import error:', error);
        setImportStatus('Error parsing file: ' + error.message);
      }
    };
    
    console.log('Starting to read file as text...');
    reader.readAsText(selectedFile);
    console.log('FileReader.readAsText called');
  }
  
  function openImportModal() {
    console.log('=== OPEN IMPORT MODAL CLICKED ===');
    setShowImportModal(true);
    setSelectedFile(null);
    setImportStatus('');
  }
  
  function closeImportModal() {
    setShowImportModal(false);
    setSelectedFile(null);
    setImportStatus('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function onAssign(nodeId, partyId) {
    // Capture current scroll position
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    setPreserveScroll(true);
    
    // Perform the assignment
    propagate(nodeId, partyId, true);
  }

  function colorFor(nodeId) {
    const a = assign[nodeId];
    if (!a || !a.partyId) return "rgba(255,255,255,0.08)";
    const party = idToParty[a.partyId];
    return party ? party.color : "rgba(255,255,255,0.08)";
  }

  function toMarkdown() {
    let md = `# Responsibility Matrix\n\n`;
    md += `**Company:** ${companyName || "Not specified"}\n\n`;
    md += `**Date:** ${new Date().toLocaleDateString()}\n\n`;
    md += `## Parties\n\n`;
    
    const namedParties = parties.filter(p => p.name.trim());
    if (namedParties.length > 0) {
      namedParties.forEach((p) => {
        md += `- **${p.name}**${p.id === "cto" ? " (CTO/CIO/CISO)" : ""}\n`;
      });
    } else {
      md += `- No parties defined yet\n`;
    }
    
    md += `\n## Responsibilities\n\n`;

    function emit(n, depth = 0) {
      if (n.id === "root") {
        n.children?.forEach((c) => emit(c, 0));
        return;
      }
      const a = assign[n.id];
      const partyName = a?.partyId ? (idToParty[a.partyId]?.name || a.partyId) : "**[Unassigned]**";
      const indent = "  ".repeat(depth);
      const explicit = a?.explicit ? " *(explicit assignment)*" : "";
      md += `${indent}- **${n.title}** → ${partyName}${explicit}\n`;
      n.children?.forEach((c) => emit(c, depth + 1));
    }

    emit(TREE);
    
    md += `\n---\n\n`;
    md += `*Generated on ${new Date().toLocaleString()}*\n`;
    
    return md;
  }

  function downloadMarkdown() {
    try {
      const md = toMarkdown();
      
      // Create blob with proper MIME type
      const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const safeCompany = (companyName || "responsibility-matrix")
        .replace(/[^a-zA-Z0-9-_\s]/g, "")
        .replace(/\s+/g, "_")
        .toLowerCase();
      const filename = `${safeCompany}_${timestamp}.md`;
      
      // Try modern approach first
      if ('showSaveFilePicker' in window) {
        // Modern File System Access API
        window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'Markdown files',
            accept: { 'text/markdown': ['.md'] }
          }]
        }).then((fileHandle) => {
          return fileHandle.createWritable();
        }).then((writable) => {
          writable.write(md);
          writable.close();
        }).catch((err) => {
          fallbackDownload();
        });
      } else {
        fallbackDownload();
      }
      
      function fallbackDownload() {
        // Traditional blob download
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        
        // Ensure the link is added to DOM before clicking
        document.body.appendChild(a);
        
        // Use setTimeout to ensure DOM update
        setTimeout(() => {
          a.click();
          
          // Clean up after a delay
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        }, 10);
      }
      
    } catch (error) {
      console.error("Download failed:", error);
      
      // Final fallback: show the markdown in a new window/tab
      const md = toMarkdown();
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`<pre>${md}</pre>`);
        newWindow.document.title = "Responsibility Matrix - Copy and Save";
      } else {
        // If popup blocked, copy to clipboard
        navigator.clipboard.writeText(md).then(() => {
          alert("Download failed, but markdown has been copied to clipboard!");
        }).catch(() => {
          alert("Download failed. Please check browser console for details.");
        });
      }
    }
  }

  const NodeRow = React.memo(({ n, level }) => {
    const a = assign[n.id] || { partyId: null, explicit: false };
    const isOpen = !!open[n.id];
    const hasChildren = n.children && n.children.length > 0;
    
    const bg = useMemo(() => {
      const c = colorFor(n.id);
      return { 
        background: `linear-gradient(to right, ${c}22, ${c}11), rgba(255,255,255,0.06)`, 
        borderColor: c 
      };
    }, [n.id, assign[n.id]?.partyId]);

    const handleToggle = useCallback(() => {
      setOpen(prev => ({ ...prev, [n.id]: !isOpen }));
    }, [n.id, isOpen]);

    const handleAssignChange = useCallback((e) => {
      e.preventDefault();
      // Prevent default scroll behavior
      e.stopPropagation();
      onAssign(n.id, e.target.value || null);
    }, [n.id]);

    return (
      <div className="rounded-xl border backdrop-blur-md shadow-sm mb-2" style={bg}>
        <div className="flex items-center gap-3 px-3 py-2" style={{ paddingLeft: 12 + level * 20 }}>
          {hasChildren ? (
            <button
              className="w-7 h-7 rounded-lg border border-white/20 hover:bg-white/10 grid place-items-center text-sm transition-all"
              onClick={handleToggle}
              title={isOpen ? "Collapse" : "Expand"}
              type="button"
            >
              {isOpen ? "–" : "+"}
            </button>
          ) : (
            <div className="w-7 h-7" />
          )}
          <div className="flex-1 font-medium text-white/90">
            {n.title}
            {a.explicit && <span className="ml-2 text-xs text-white/70">(explicit)</span>}
          </div>
          <select
            className="min-w-[14rem] bg-white/10 text-white/90 border border-white/20 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-white/40"
            value={a.partyId || ""}
            onChange={handleAssignChange}
          >
            <option value="">Unassigned</option>
            {selectableParties.map((p) => (
              <option key={p.id} value={p.id} style={{ backgroundColor: "#0b1220", color: "#e5e7eb" }}>
                {p.name ? p.name : `[${p.id}]`} {p.confirmed ? "✓" : ""}
              </option>
            ))}
          </select>
          <div 
            className="w-4 h-4 rounded-full ml-2 border border-white/30" 
            style={{ background: idToParty[a.partyId || ""]?.color || "transparent" }} 
            title={idToParty[a.partyId || ""]?.name} 
          />
        </div>
        {isOpen && hasChildren && (
          <div className="pl-0 pr-2 pb-2">
            {n.children.map((c) => (
              <NodeRow key={c.id} n={c} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(1200px_600px_at_100%_-10%,#3b82f650,transparent),radial-gradient(800px_400px_at_-10%_10%,#22c55e40,transparent)] bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <header className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-semibold tracking-tight">Responsibility Assignment Builder</h1>
            <button
              onClick={openImportModal}
              className="px-4 py-2 text-sm rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all hover:scale-105 flex items-center gap-2"
            >
              <span>📁</span> Import .md File
            </button>
          </div>
        </header>
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 mb-6">
          <label className="block text-sm text-white/70 mb-1">Company Name</label>
          <input
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40"
            placeholder="Enter company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>
        <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Parties</h2>
            <button onClick={addParty} className="text-sm px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/10">
              + Add Party
            </button>
          </div>
          <div className="space-y-2">
            {parties.map((p, i) => (
              <div
                key={p.id}
                className="flex items-center gap-3 p-2 rounded-xl border border-white/10"
                style={{ background: `${p.color}22`, boxShadow: `inset 0 0 0 1px ${p.color}33` }}
              >
                <div className="w-2 h-8 rounded-full" style={{ background: p.color }} />
                <div className="min-w-[14ch] text-white/80 text-sm">
                  {p.id === "cto" ? "CTO/CIO/CISO Name:" : `IT PARTY ${i}`}
                </div>
                <input
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40"
                  placeholder={p.id === "cto" ? "Type name..." : "Party name..."}
                  value={p.name}
                  onChange={(e) =>
                    setParties((prev) => prev.map((x) => (x.id === p.id ? { ...x, name: e.target.value } : x)))
                  }
                />
                {p.id === "cto" ? (
                  <button
                    onClick={confirmCTO}
                    className={`text-xs px-2.5 py-1.5 rounded-lg border ${p.confirmed ? "bg-white/20" : "hover:bg-white/10"} border-white/20`}
                    title="Confirm CTO/CIO/CISO"
                  >
                    ✓ Confirm
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => confirmParty(p.id)}
                      className={`text-xs px-2.5 py-1.5 rounded-lg border ${p.confirmed ? "bg-white/20" : "hover:bg-white/10"} border-white/20`}
                      title="Confirm party"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => deleteParty(p.id)}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-white/20 hover:bg-white/10"
                      title="Remove party"
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
        
        <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Line Of Business Apps</h2>
            <button onClick={addLobApp} className="text-sm px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/10">
              + Add App
            </button>
          </div>
          <div className="space-y-2">
            {lobApps.map((app, i) => (
              <div
                key={app.id}
                className="flex items-center gap-3 p-2 rounded-xl border border-white/10 bg-blue-500/10"
                style={{ boxShadow: `inset 0 0 0 1px rgba(59, 130, 246, 0.2)` }}
              >
                <div className="w-2 h-8 rounded-full bg-blue-500" />
                <div className="min-w-[14ch] text-white/80 text-sm">
                  {app.id === "app1" ? "APP 1:" : app.id === "app2" ? "APP 2:" : `APP ${i + 1}:`}
                </div>
                <input
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40"
                  placeholder="Application name..."
                  value={app.name}
                  onChange={(e) =>
                    setLobApps((prev) => prev.map((x) => (x.id === app.id ? { ...x, name: e.target.value } : x)))
                  }
                />
                <button
                  onClick={() => confirmLobApp(app.id)}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border ${app.confirmed ? "bg-white/20" : "hover:bg-white/10"} border-white/20`}
                  title="Confirm application"
                >
                  ✓
                </button>
                {lobApps.length > 1 && (
                  <button
                    onClick={() => deleteLobApp(app.id)}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-white/20 hover:bg-white/10"
                    title="Remove application"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
        
        <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">OTHER Items</h2>
            <button onClick={addOtherItem} className="text-sm px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/10">
              + Add Item
            </button>
          </div>
          <div className="space-y-2">
            {otherItems.map((item, i) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-2 rounded-xl border border-white/10 bg-purple-500/10"
                style={{ boxShadow: `inset 0 0 0 1px rgba(168, 85, 247, 0.2)` }}
              >
                <div className="w-2 h-8 rounded-full bg-purple-500" />
                <div className="min-w-[14ch] text-white/80 text-sm">
                  {item.id === "other1" ? "OTHER 1:" : item.id === "other2" ? "OTHER 2:" : `OTHER ${i + 1}:`}
                </div>
                <input
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40"
                  placeholder="Other item name..."
                  value={item.name}
                  onChange={(e) =>
                    setOtherItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, name: e.target.value } : x)))
                  }
                />
                <button
                  onClick={() => confirmOtherItem(item.id)}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border ${item.confirmed ? "bg-white/20" : "hover:bg-white/10"} border-white/20`}
                  title="Confirm other item"
                >
                  ✓
                </button>
                {otherItems.length > 1 && (
                  <button
                    onClick={() => deleteOtherItem(item.id)}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-white/20 hover:bg-white/10"
                    title="Remove other item"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
        <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 mb-6">
          <h2 className="text-xl font-semibold mb-3">Responsibilities</h2>
          <div className="space-y-2">
            {TREE.children?.map((n) => (
              <NodeRow key={n.id} n={n} level={0} />
            ))}
          </div>
        </section>
        <footer className="flex items-center justify-between gap-4">
          <div className="text-white/60 text-sm">
            Tip: Assign at any level to propagate. Explicit assignments are labeled.
          </div>
          <button
            onClick={downloadMarkdown}
            className="px-4 py-2 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all hover:scale-105"
          >
            Submit & Download Markdown
          </button>
        </footer>
        
        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Import Responsibility Matrix</h3>
                <button
                  onClick={closeImportModal}
                  className="w-8 h-8 rounded-lg border border-white/20 hover:bg-white/10 grid place-items-center text-white transition-all"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Select .md file to import</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.markdown"
                    onChange={handleFileSelect}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white file:bg-white/20 file:border-0 file:rounded file:px-2 file:py-1 file:text-white/90 file:mr-3 hover:bg-white/20 transition-all"
                  />
                </div>
                
                {selectedFile && (
                  <div className="text-sm text-white/80">
                    Selected: <span className="font-medium">{selectedFile.name}</span>
                  </div>
                )}
                
                {importStatus && (
                  <div className={`text-sm p-2 rounded-lg ${
                    importStatus.includes('Successfully') 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {importStatus}
                  </div>
                )}
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      console.log('=== PROCESS FILE BUTTON CLICKED ===');
                      processImportFile();
                    }}
                    disabled={!selectedFile}
                    className="flex-1 px-4 py-2 rounded-xl border border-white/20 bg-blue-500/20 hover:bg-blue-500/30 disabled:bg-white/10 disabled:text-white/50 disabled:cursor-not-allowed text-white transition-all"
                  >
                    Process File
                  </button>
                  <button
                    onClick={closeImportModal}
                    className="px-4 py-2 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;